import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfirmationModalConfig } from 'shared/components/confirmation-modal/confirmation-modal-config';
import { ClosingConfirmationService } from 'shared/components/confirmation-modal/confirmation-modal.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import {
  closeMessageConversation,
  loadWrapUpsForMessageConversation,
  saveWrapUpForMessageConversation,
  skipWrapUpForMessageConversation
} from 'common/store/genesys-cloud/conversations/messages/actions/message-conversation.actions';
import {
  sendMediaMessage,
  sendMediaMessageFailure,
  sendMediaMessageSuccess,
  uploadMedia,
  uploadMediaFailure,
  uploadMediaSuccess
} from 'common/store/genesys-cloud/conversations/messages/actions/message-media.actions';
import {
  sendMessage,
  sendTypingIndicator
} from 'common/store/genesys-cloud/conversations/messages/actions/message.actions';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { filter, map, Observable, Subject, takeUntil } from 'rxjs';
import { WrapUpCode } from 'common/store/genesys-cloud/conversations/common/models/wrap-up-code.model';
import { selectWrapUpsByConversationId } from 'common/store/genesys-cloud/conversations/messages/selectors/message-conversation.selectors';
import { getMessageDraftStorageKey } from 'common/store/genesys-cloud/conversations/messages/helpers/message-drafts.helpers';
import { CommunicationType } from 'common/store/genesys-cloud/conversations/common/models/communication-type.enum';
import { DomSanitizer } from '@angular/platform-browser';
import { GenesysChatFileUploadStore } from 'components/work-item-lane-list/work-item-lane/work-item-widget/genesys-chat-widget/genesys-chat-box/genesys-chat-footer/genesys-chat-file-upload.store';
import { Actions, ofType } from '@ngrx/effects';
import {
  FooterSettings,
  selectFooterSettings,
  selectIsUrlImagesEnabled
} from 'common/store/genesys-cloud/feature-toggles/selectors/footer-settings-selector';
import { Turndown } from 'shared/services/turndown-wrapper/turndown.service';
import { BcConfigService } from 'common/config/provider';
import { MessageType } from 'common/store/genesys-cloud/conversations/common/models/message-type.enum';

interface EditorContentModel {
  html: string;
}

@Component({
  selector: 'app-genesys-chat-footer',
  providers: [GenesysChatFileUploadStore],
  templateUrl: './genesys-chat-footer.component.html',
  styleUrls: ['./genesys-chat-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysChatFooterComponent implements OnInit, OnChanges, OnDestroy {
  readonly confirmationBoxConfig: ConfirmationModalConfig = {
    title: 'gcChatBox.endChatConfirmation.title',
    subtitle: 'gcChatBox.endChatConfirmation.subtitle',
    buttonTitle: 'gcChatBox.endChatConfirmation.yes',
    secondaryActionTitle: 'gcChatBox.endChatConfirmation.endChatDontShowAgain',
    cancelTitle: 'gcChatBox.endChatConfirmation.continue'
  };

  settings$: Observable<FooterSettings>;
  readonly communicationTypes = CommunicationType;

  confirmationVisible = false;
  isMoreActionsVisible = false;
  isWrapUpOpen = false;
  autoSaveStorageKey = '';
  editorContent: EditorContentModel = {
    html: ''
  };

  selectedWrapUpCode: WrapUpCode;
  wrapUpCodes$: Observable<WrapUpCode[]>;

  readonly fileUploadState$ = this.fileStore.fileUploadState$;

  @Input()
  conversationId: string;
  @Input()
  isExpandedMode: boolean;
  @Input()
  isFocused: boolean;
  @Input()
  isConversationOpen: boolean;
  @Input()
  isWrapUpRequired: boolean;
  @Input()
  isConversationAcceptable: boolean;
  @Input()
  isMessagingPeriodEnded: boolean;
  @Input()
  conversationType: MessageType;
  @Input()
  isCobrowseActive: boolean;
  @Output()
  editorContentChange = new EventEmitter<string>();
  @Output()
  openTransferPanel = new EventEmitter<void>();
  @Input()
  disableFileUpload: boolean;

  private readonly onDestroySubject = new Subject<void>();

  isUrlImagesEnabled$: Observable<boolean>;

  constructor(
    readonly store: Store,
    private readonly actions: Actions,
    readonly fileStore: GenesysChatFileUploadStore,
    readonly changeDetectorRef: ChangeDetectorRef,
    private readonly closingConfirmationService: ClosingConfirmationService,
    private readonly rxEmitter: RxEmitter,
    private readonly sanitizer: DomSanitizer,
    private readonly turndownService: Turndown,
    private readonly config: BcConfigService
  ) {
    actions
      .pipe(
        ofType(uploadMediaSuccess),
        filter(({ conversationId }) => conversationId === this.conversationId),
        map(({ mediaId, status }) => this.fileStore.fileUploadSuccess({ mediaId, status })),
        takeUntil(this.onDestroySubject)
      )
      .subscribe();

    actions
      .pipe(
        ofType(...[uploadMediaFailure, sendMediaMessageFailure]),
        filter(({ conversationId }) => conversationId === this.conversationId),
        map(() => this.fileStore.fileUploadFailure()),
        takeUntil(this.onDestroySubject)
      )
      .subscribe();

    actions
      .pipe(
        ofType(sendMediaMessageSuccess),
        filter(({ conversationId }) => conversationId === this.conversationId),
        map(() => this.fileStore.deleteFile()),
        takeUntil(this.onDestroySubject)
      )
      .subscribe();
  }

  ngOnInit() {
    this.store.dispatch(loadWrapUpsForMessageConversation({ conversationId: this.conversationId }));
    this.wrapUpCodes$ = this.store.select(selectWrapUpsByConversationId(this.conversationId));

    this.settings$ = this.store.select(
      selectFooterSettings(this.conversationId, this.config.current.genesysRichTextEnabledProviders)
    );
    this.isUrlImagesEnabled$ = this.store.select(
      selectIsUrlImagesEnabled(this.conversationId, this.config.current.genesysRichTextEnabledProviders)
    );
  }

  ngOnChanges({ conversationId, isMessagingPeriodEnded }: SimpleChanges) {
    if (conversationId) {
      this.autoSaveStorageKey = getMessageDraftStorageKey(conversationId.currentValue);
    }

    if (isMessagingPeriodEnded?.currentValue) {
      this.fileStore.deleteFile();
      this._clearEditorContent();
    }
  }

  ngOnDestroy() {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  selectWrapUp(wrapUpCode: WrapUpCode) {
    this.selectedWrapUpCode = wrapUpCode;
  }

  saveWrapUp(wrapUpCode: WrapUpCode) {
    this.store.dispatch(
      saveWrapUpForMessageConversation({ conversationId: this.conversationId, wrapUpCode: wrapUpCode.id })
    );
  }

  skipWrapUp() {
    this.store.dispatch(skipWrapUpForMessageConversation({ conversationId: this.conversationId }));
  }

  toggleMoreActions(): void {
    this.isMoreActionsVisible = !this.isMoreActionsVisible;
  }

  toggleWrapUps(): void {
    this.isWrapUpOpen = !this.isWrapUpOpen;
  }

  private _clearEditorContent(): void {
    this.editorContent.html = '';
    this.editorContentChange.emit('');
  }

  get isWrapUpVisible(): boolean {
    return this.isWrapUpOpen || !this.isConversationOpen;
  }

  get isEditorVisible(): boolean {
    return !this.isWrapUpVisible && (!this.isMoreActionsVisible || this.isExpandedMode);
  }

  onConversationEnded(): void {
    this.store.dispatch(closeMessageConversation({ conversationId: this.conversationId }));
    this.confirmationVisible = false;
  }

  get isConfirmationModalDisabled(): boolean {
    return <boolean>this.closingConfirmationService.getClosingConfirmationSetting(WorkItemTypes.GENESYS_CHAT);
  }

  showConfirmation(): void {
    this.confirmationVisible = true;
  }

  closeConfirmation(): void {
    this.confirmationVisible = false;
  }

  onEditorSubmit(content: string, richTextEnabled: boolean): void {
    const trimmed = content?.trim();
    if (trimmed) {
      this.store.dispatch(
        sendMessage({
          conversationId: this.conversationId,
          textBody: this._getMessageText(richTextEnabled, trimmed)
        })
      );
      this._clearEditorContent();
    }
  }

  setTyping(): void {
    this.store.dispatch(sendTypingIndicator({ conversationId: this.conversationId }));
  }

  onMediaSend(mediaId: string): void {
    this.store.dispatch(sendMediaMessage({ conversationId: this.conversationId, mediaIds: [mediaId] }));
  }

  onAutoSaveModelChange({ html }: EditorContentModel): void {
    const sanitizedHtml = this.sanitizer.sanitize(SecurityContext.HTML, html);
    this.editorContentChange.emit(sanitizedHtml);
  }

  onEditorInitialized(): void {
    this.setFocusOnEditor();
  }

  onTransferPanelOpen(): void {
    this.openTransferPanel.emit();
  }

  endOrShowConfirmation(): void {
    if (this.isConfirmationModalDisabled) {
      this.endConversation();
    } else {
      this.showConfirmation();
    }
  }

  endConversationAndTurnOffModal(): void {
    this.closingConfirmationService.setClosingConfirmation(WorkItemTypes.GENESYS_CHAT, true);
    this.rxEmitter.send(MessageBusEvents.ShowConfirmationPanelTooltip, null);
    this.onConversationEnded();
  }

  uploadFile(file: File): void {
    this.fileStore.addFile(file);

    const fd = new FormData();
    fd.append('file', file);
    this.store.dispatch(uploadMedia({ conversationId: this.conversationId, file: fd }));
  }

  deleteFile(): void {
    this.fileStore.deleteFile();
  }

  private _getMessageText(richTextEnabled: boolean, text: string): string {
    const removeEscapeCharacter = this.config.current.genesysRichTextEnabledProviders.some(
      (p) => !p.escapingEnabled && p.type === this.conversationType
    );
    return richTextEnabled ? this.turndownService.convert(text, removeEscapeCharacter) : text;
  }

  private endConversation(): void {
    this.closeConfirmation();
    this.onConversationEnded();
  }

  private setFocusOnEditor(): void {
    this.rxEmitter.send(broadcastEvents.focusEditor, null);
  }
}
